// Backlit stages: the light sits behind the fighters (the lit kamiza, a setting
// sun, a huge moon) so they read as ink silhouettes with a rim of light.
// Everything static is painted once into an offscreen canvas; ambient motion
// (drifting motes/petals, the TV tower's beacon) is drawn per frame.
//
// Red–orange belts train in the dojo, green–blue at sunset in the park,
// purple–black (and Kumite) on a rooftop under the moon. The dojo is the
// player's own: each partner dojo has its room (room-*.ts), loaded when
// first needed so players download only their own (and not at the start).

import type { DojoStyle } from '@/content/dojos';
import { loadLogo } from './logo';

export type StageId = 'dojo' | 'park' | 'rooftop';

export interface StageView {
  w: number;
  h: number;
  groundY: number;
  ppu: number;
  dpr: number;
  /** Text for the dojo's name board; dojo stage only. */
  sign: string;
  /** Which room the dojo stage shows. */
  style: DojoStyle;
}

export interface Theme {
  /** Silhouette ink for every fighter on this stage. */
  ink: string;
  /** Backlight edge colour. */
  rim: string;
  /** Where the light source sits, as a fraction of the width. */
  lightX: number;
  /** Polished floor: fighters get a faint reflection. */
  reflect: boolean;
  /** How sparks and flashes mix: additive glow on dark stages; plain on the
   *  bright dojo, where adding light would wash out to white. */
  blend: 'lighter' | 'source-over';
  particles: { color: string; kind: 'mote' | 'petal' | 'speck'; count: number };
  /** Depth of field: blur (world units) for everything behind the fighters' feet. */
  blur: number;
}

export const THEMES: Record<StageId, Theme> = {
  dojo: {
    ink: '#1c120d',
    rim: '#fff4de',
    lightX: 0.5,
    reflect: false, // matte mats
    blend: 'source-over',
    particles: { color: 'rgba(255,250,235,0.6)', kind: 'mote', count: 18 },
    blur: 0.035,
  },
  park: {
    ink: '#1a0f24',
    rim: '#ffd38f',
    lightX: 0.5,
    reflect: false,
    blend: 'lighter',
    particles: { color: '#ffa9c2', kind: 'petal', count: 22 },
    blur: 0,
  },
  rooftop: {
    ink: '#080a1a',
    rim: '#d6e2ff',
    lightX: 0.62,
    reflect: true,
    blend: 'lighter',
    particles: { color: 'rgba(210,225,255,0.5)', kind: 'speck', count: 18 },
    blur: 0,
  },
};

/** The other dojo rooms; `kamiza` is THEMES.dojo. */
const ROOM_THEMES: Record<Exclude<DojoStyle, 'kamiza'>, Theme> = {
  hall: {
    ink: '#161a22',
    rim: '#f6f8ff',
    lightX: 0.5,
    reflect: false,
    blend: 'source-over',
    // Dust in the daylight from the windows and the skylight
    particles: { color: 'rgba(255,255,255,0.6)', kind: 'mote', count: 16 },
    blur: 0.03,
  },
  mural: {
    ink: '#1c1418',
    rim: '#fff1dc',
    lightX: 0.5,
    reflect: false,
    blend: 'source-over',
    // Petals drifting off the painted cherry tree
    particles: { color: '#f6a5bd', kind: 'petal', count: 10 },
    blur: 0.035,
  },
  frost: {
    ink: '#151a17',
    rim: '#f6f8ff',
    lightX: 0.5,
    reflect: false, // matte tatami
    blend: 'source-over',
    // Dust in the daylight through the frosted glass
    particles: { color: 'rgba(255,255,255,0.6)', kind: 'mote', count: 14 },
    blur: 0.035,
  },
  station: {
    ink: '#1e1216',
    rim: '#fff2ea',
    lightX: 0.5,
    reflect: false, // matte mats
    blend: 'source-over',
    // Dust dancing in the sunbeams
    particles: { color: 'rgba(255,244,232,0.65)', kind: 'mote', count: 16 },
    blur: 0.035,
  },
};

/** The look of a stage; the dojo stage depends on the player's dojo. */
export function themeFor(stage: StageId, style: DojoStyle): Theme {
  return stage === 'dojo' && style !== 'kamiza' ? ROOM_THEMES[style] : THEMES[stage];
}

/** Deterministic pseudo-random in [0,1) so skylines are stable across repaints. */
export function hash(n: number): number {
  const x = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
}

export type Ctx = CanvasRenderingContext2D;

/** A lit window in the skyline: x, y, width, height (CSS px). */
export type WindowRect = [number, number, number, number];

/** What the ambience animates on a painted backdrop: windows that go dark, blinking beacons. */
export interface Lights {
  windows: WindowRect[];
  /** x, y and glow radius (CSS px). */
  beacons: [number, number, number][];
}

/** Filters a list in place (others hold it). */
function keep<T>(list: T[], ok: (t: T) => boolean): void {
  let n = 0;
  for (const t of list) if (ok(t)) list[n++] = t;
  list.length = n;
}

/** Drops the lights a shape painted over them hides: windows it overlaps, beacons inside it. */
export function cover(
  { windows, beacons }: Lights,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  const clear = (px: number, py: number, pw = 0, ph = 0): boolean =>
    px >= x + w || px + pw <= x || py >= y + h || py + ph <= y;
  keep(windows, (r) => clear(...r));
  keep(beacons, ([bx, by]) => clear(bx, by));
}

export function vgrad(ctx: Ctx, y0: number, y1: number, stops: [number, string][]): CanvasGradient {
  const g = ctx.createLinearGradient(0, y0, 0, y1);
  for (const [at, c] of stops) g.addColorStop(at, c);
  return g;
}

export function glow(ctx: Ctx, x: number, y: number, r: number, inner: string): void {
  const g = ctx.createRadialGradient(x, y, 0, x, y, r);
  g.addColorStop(0, inner);
  g.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = g;
  ctx.fillRect(x - r, y - r, r * 2, r * 2);
}

/** How far back the kamiza wall stands: drawn this much smaller, its base this far up. */
const WALL_SCALE = 0.78;
const WALL_SETBACK = 0.55;

/** The view of the back wall: set back, so smaller and its foot up the floor. */
export function wallView(view: StageView): StageView {
  return {
    ...view,
    groundY: view.groundY - view.ppu * WALL_SETBACK,
    ppu: view.ppu * WALL_SCALE,
  };
}

function dojo(ctx: Ctx, view: StageView, _lights: Lights, logo: HTMLImageElement | null): void {
  const room = rooms.get(view.style);
  // The wall stands well behind the fighters: smaller, its foot up the mats
  if (room) return room(ctx, wallView(view), logo);
  // Still on its way, or it couldn't load (offline before the game was cached)
  if (room === undefined) void loadRoom(view.style);
  blankRoom(ctx, view);
}

type Outdoor = (ctx: Ctx, v: StageView, lights: Lights) => void;

/** The outdoor stages and the Berlin they look out on (berlin.ts, a chunk of its own); null until it has loaded. */
let outdoor: { park: Outdoor; rooftop: Outdoor } | null = null;
let cityLoad: Promise<boolean> | null = null;

/** Load the park, the rooftop and their skyline (once; retried if it failed): whether they're in. */
export function loadCity(): Promise<boolean> {
  cityLoad ??= import('./berlin').then(
    (m) => {
      outdoor = { park: m.parkStage, rooftop: m.rooftopStage };
      return true;
    },
    () => {
      cityLoad = null;
      return false;
    },
  );
  return cityLoad;
}

/** Until the outdoor stages have loaded: their sky over their ground, nothing else. */
function blankOutdoor(ctx: Ctx, v: StageView, stage: 'park' | 'rooftop'): void {
  void loadCity();
  const { w, h, groundY: g, ppu } = v;
  const horizon = g - ppu * (stage === 'park' ? 0.9 : 0.38);
  ctx.fillStyle = vgrad(
    ctx,
    0,
    horizon,
    stage === 'park'
      ? [
          [0, '#231544'],
          [1, '#ffc27a'],
        ]
      : [
          [0, '#050820'],
          [1, '#4a3b7c'],
        ],
  );
  ctx.fillRect(0, 0, w, horizon);
  ctx.fillStyle = THEMES[stage].ink;
  ctx.fillRect(0, horizon, w, h - horizon);
}

export const ROOF_TOWER_X = 0.2;
export const ROOF_TOWER_H = 4.4;
/** Front skyline colour (a window switched off is painted with it). */
export const ROOF_FRONT = '#15123a';

type Paint = (ctx: Ctx, v: StageView, lights: Lights, logo: HTMLImageElement | null) => void;

type Room = (ctx: Ctx, v: StageView, logo: HTMLImageElement | null) => void;

/** The partner dojos' rooms, each its own chunk. */
const ROOMS: Record<DojoStyle, () => Promise<Room>> = {
  kamiza: () => import('./room-kamiza').then((m) => m.kamizaRoom),
  hall: () => import('./room-hall').then((m) => m.hallRoom),
  mural: () => import('./room-mural').then((m) => m.muralRoom),
  frost: () => import('./room-frost').then((m) => m.frostRoom),
  station: () => import('./room-station').then((m) => m.stationRoom),
};

/** Loaded rooms; null for one that failed to load. */
const rooms = new Map<DojoStyle, Room | null>();
const loading = new Map<DojoStyle, Promise<void>>();

/** Fetches a dojo's room ahead of its first paint; resolves once it's loaded or has failed. */
export function loadRoom(style: DojoStyle): Promise<void> {
  if (rooms.get(style)) return Promise.resolve();
  if (rooms.get(style) === null) {
    // Failed before: try again
    rooms.delete(style);
    loading.delete(style);
  }
  let p = loading.get(style);
  if (!p) {
    p = ROOMS[style]().then(
      (room) => void rooms.set(style, room),
      () => void rooms.set(style, null),
    );
    loading.set(style, p);
  }
  return p;
}

/** Whether a dojo's room is loaded (or known to fail), so its backdrop is final. */
function roomSettled(style: DojoStyle): boolean {
  return rooms.has(style);
}

/** Until a room has loaded: a plain wall lit behind the fighters and dark mats, no dojo's likeness. */
function blankRoom(ctx: Ctx, view: StageView): void {
  const { w, h, groundY: g, ppu: u } = wallView(view);
  const wall = ctx.createRadialGradient(w / 2, g - u * 1.7, u, w / 2, g - u * 1.7, u * 5);
  wall.addColorStop(0, '#f2ece4');
  wall.addColorStop(1, '#5d5550');
  ctx.fillStyle = wall;
  ctx.fillRect(0, 0, w, g);
  ctx.fillStyle = vgrad(ctx, g, h, [
    [0, '#46404a'],
    [1, '#141218'],
  ]);
  ctx.fillRect(0, g, w, h - g);
}

const PAINT: Record<StageId, Paint> = {
  dojo,
  park: (ctx, v, lights) => (outdoor ? outdoor.park(ctx, v, lights) : blankOutdoor(ctx, v, 'park')),
  rooftop: (ctx, v, lights) =>
    outdoor ? outdoor.rooftop(ctx, v, lights) : blankOutdoor(ctx, v, 'rooftop'),
};

let blurSupport: boolean | null = null;

/**
 * Whether the canvas `filter` really blurs: some browsers expose the property
 * but ignore it. Blurs one white pixel and checks that it spread.
 */
function canvasBlurWorks(): boolean {
  if (blurSupport !== null) return blurSupport;
  blurSupport = false;
  try {
    const t = document.createElement('canvas');
    t.width = 9;
    t.height = 1;
    const g = t.getContext('2d', { willReadFrequently: true });
    if (g && typeof (g as { filter?: unknown }).filter === 'string') {
      g.filter = 'blur(2px)';
      g.fillStyle = '#fff';
      g.fillRect(4, 0, 1, 1);
      blurSupport = (g.getImageData(2, 0, 1, 1).data[3] ?? 0) > 0;
    }
  } catch {
    blurSupport = false;
  }
  return blurSupport;
}

/**
 * Blur without a canvas filter: halve the image a few times, then double it
 * back up. Each step is a small bilinear resample, so the result is smooth,
 * where a single big shrink-and-stretch would show blocks.
 */
function mipBlur(src: HTMLCanvasElement, blur: number): HTMLCanvasElement {
  const levels: HTMLCanvasElement[] = [src];
  let cur = src;
  for (let k = 2; k <= Math.max(2, blur); k *= 2) {
    const n = document.createElement('canvas');
    n.width = Math.max(1, Math.round(cur.width / 2));
    n.height = Math.max(1, Math.round(cur.height / 2));
    const g = n.getContext('2d');
    if (!g) break;
    g.imageSmoothingQuality = 'high';
    g.drawImage(cur, 0, 0, n.width, n.height);
    levels.push(n);
    cur = n;
  }
  // Back up through the same sizes
  for (let i = levels.length - 2; i >= 0; i--) {
    const target = levels[i]!;
    const n = document.createElement('canvas');
    n.width = target.width;
    n.height = target.height;
    const g = n.getContext('2d');
    if (!g) break;
    g.imageSmoothingQuality = 'high';
    g.drawImage(cur, 0, 0, n.width, n.height);
    cur = n;
  }
  return cur;
}

/**
 * Depth of field on a painted backdrop (device pixels): everything above the
 * fighters' feet at `feetY` is blurred, fading back to sharp just in front of
 * them. Uses the canvas blur filter where it works, else `mipBlur`.
 */
function defocus(c: HTMLCanvasElement, feetY: number, unit: number, blur: number): void {
  const { width: w, height: h } = c;
  const soft = document.createElement('canvas');
  soft.width = w;
  soft.height = h;
  const s = soft.getContext('2d');
  const out = c.getContext('2d');
  if (!s || !out) return;
  if (canvasBlurWorks()) {
    s.filter = `blur(${blur}px)`;
    s.drawImage(c, 0, 0);
    s.filter = 'none';
  } else s.drawImage(mipBlur(c, blur), 0, 0);
  // Sharp again from just behind the feet down to the viewer
  const fade = s.createLinearGradient(0, feetY - unit * 0.35, 0, feetY + unit * 0.05);
  fade.addColorStop(0, 'rgba(0,0,0,0)');
  fade.addColorStop(1, 'rgba(0,0,0,1)');
  s.globalCompositeOperation = 'destination-out';
  s.fillStyle = fade;
  s.fillRect(0, feetY - unit * 0.35, w, h);
  s.globalCompositeOperation = 'source-over';
  out.save();
  out.setTransform(1, 0, 0, 1, 0, 0);
  out.drawImage(soft, 0, 0);
  out.restore();
}

/** A small still of one dojo room for the picker's cards (CSS pixels w × h). */
export async function roomThumb(
  style: DojoStyle,
  sign: string,
  w: number,
  h: number,
): Promise<HTMLCanvasElement> {
  const [logo] = await Promise.all([loadLogo().catch(() => null), loadRoom(style)]);
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const c = document.createElement('canvas');
  c.width = Math.round(w * dpr);
  c.height = Math.round(h * dpr);
  const ctx = c.getContext('2d');
  if (!ctx) return c;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  // Framed a little closer than the fight: the room, not the empty floor
  const view: StageView = { w, h, groundY: h * 0.9, ppu: h / 3.4, dpr, sign, style };
  dojo(ctx, view, { windows: [], beacons: [] }, logo);
  return c;
}

/** Paints and caches one backdrop per (stage, room, size, sign), plus a vignette. */
export class Backdrop {
  private canvas: HTMLCanvasElement | null = null;
  private vignette: HTMLCanvasElement | null = null;
  private key = '';
  private vignetteKey = '';
  /** Lit windows and beacons of the current backdrop, for the ambience. */
  lights: Lights = { windows: [], beacons: [] };
  /** The club emblem for the dojo wall; the backdrop repaints once it loads. */
  private logo: HTMLImageElement | null = null;

  constructor() {
    loadLogo()
      .then((img) => (this.logo = img))
      .catch(() => undefined);
  }

  draw(ctx: Ctx, stage: StageId, v: StageView): void {
    // Nothing to paint at no size (and the city's streets would never end)
    if (!(v.ppu > 0)) return;
    // Repaints once the logo, the room or the city have arrived
    const settled = stage === 'dojo' ? roomSettled(v.style) : !!outdoor;
    const ready = `${this.logo ? 1 : 0}${settled ? 1 : 0}`;
    const key = `${stage}|${v.style}|${v.w}|${v.h}|${v.dpr}|${v.sign}|${ready}`;
    if (key !== this.key || !this.canvas) {
      const c = this.canvas ?? document.createElement('canvas');
      c.width = Math.round(v.w * v.dpr);
      c.height = Math.round(v.h * v.dpr);
      const off = c.getContext('2d');
      if (!off) return;
      off.setTransform(v.dpr, 0, 0, v.dpr, 0, 0);
      this.lights = { windows: [], beacons: [] };
      PAINT[stage](off, v, this.lights, this.logo);
      // Only the windows in sight switch on and off
      keep(this.lights.windows, ([x, , ww]) => x + ww > 0 && x < v.w);
      const blur = themeFor(stage, v.style).blur * v.ppu * v.dpr;
      if (blur > 0) defocus(c, v.groundY * v.dpr, v.ppu * v.dpr, blur);
      this.canvas = c;
      this.key = key;
    }
    ctx.drawImage(this.canvas, 0, 0, v.w, v.h);
  }

  /** Darkened corners that pull the eye to the fighters (drawn over them). */
  drawVignette(ctx: Ctx, v: StageView): void {
    const key = `${v.w}|${v.h}`;
    if (key !== this.vignetteKey || !this.vignette) {
      // Low resolution is fine for a smooth gradient
      const c = this.vignette ?? document.createElement('canvas');
      c.width = Math.max(1, Math.round(v.w / 4));
      c.height = Math.max(1, Math.round(v.h / 4));
      const off = c.getContext('2d');
      if (!off) return;
      const cx = c.width / 2;
      const cy = (v.groundY - v.ppu) / 4;
      const r = Math.hypot(c.width, c.height) * 0.62;
      const g = off.createRadialGradient(cx, cy, r * 0.45, cx, cy, r);
      g.addColorStop(0, 'rgba(0,0,0,0)');
      g.addColorStop(1, 'rgba(0,0,0,0.38)');
      off.clearRect(0, 0, c.width, c.height);
      off.fillStyle = g;
      off.fillRect(0, 0, c.width, c.height);
      this.vignette = c;
      this.vignetteKey = key;
    }
    ctx.drawImage(this.vignette, 0, 0, v.w, v.h);
  }
}

/** Per-frame ambience: drifting particles, the beacons on the TV tower and the crane, windows. */
export function drawAmbient(
  ctx: Ctx,
  stage: StageId,
  v: StageView,
  time: number,
  { windows, beacons }: Lights = { windows: [], beacons: [] },
): void {
  const p = themeFor(stage, v.style).particles;
  const { w, groundY: g, ppu } = v;
  ctx.fillStyle = p.color;
  for (let i = 0; i < p.count; i++) {
    const speed = 0.2 + hash(i * 3.7) * 0.5;
    const phase = hash(i * 1.3);
    if (p.kind === 'petal') {
      // Petals drift down and sideways, tumbling
      const k = (time * speed * 0.12 + phase) % 1;
      const x =
        ((hash(i * 5.3) + time * 0.02 * speed) % 1) * w + Math.sin(time * 1.5 + i) * ppu * 0.3;
      ctx.save();
      ctx.translate(x, k * (g + ppu * 0.5));
      ctx.rotate(time * 2 + i);
      ctx.beginPath();
      ctx.ellipse(0, 0, ppu * 0.05, ppu * 0.025, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    } else {
      // Motes / specks rise slowly through the light
      const k = (time * speed * 0.05 + phase) % 1;
      const x = hash(i * 5.3) * w + Math.sin(time * 0.7 + i) * ppu * 0.15;
      const r = (p.kind === 'mote' ? 1.6 : 1.1) * (0.6 + hash(i * 2.2));
      ctx.globalAlpha = Math.sin(k * Math.PI);
      ctx.beginPath();
      ctx.arc(x, g - k * g, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
    }
  }
  if (Math.floor(time * 1.2) % 2 === 0) {
    // The beacons blink
    for (const [bx, by, r] of beacons) glow(ctx, bx, by, r, 'rgba(255,70,70,0.9)');
  }
  if (stage === 'rooftop' && windows.length) {
    // The city lives: a few windows go dark, now and then others
    ctx.fillStyle = ROOF_FRONT;
    for (let i = 0; i < 4; i++) {
      const slot = Math.floor(time * 0.2 + hash(i * 4.7) * 3);
      const rect = windows[Math.floor(hash(i * 9.3 + slot * 1.7) * windows.length)];
      if (rect) ctx.fillRect(...rect);
    }
  }
}
